import { faRobot } from "@fortawesome/pro-duotone-svg-icons";
import {
  faArrowsToCircle,
  faCog,
  faFloppyDisk,
  faHistory,
  faLoader,
  faMoneyCheckPen,
  faPlus,
  faRotate,
  faSpinner,
  faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { faCaretDown, faSend, faStars } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Dialog from "@radix-ui/react-dialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Editor, EditorContent, generateJSON, JSONContent, useEditor } from "@tiptap/react";
import classNames from "classnames";
import DOMPurify from "dompurify";
import { encode } from "gpt-tokenizer";
import { marked } from "marked";
import { nanoid } from "nanoid";
import { forwardRef, useEffect, useReducer, useRef, useState } from "react";
import ReactDiffViewer from "react-diff-viewer";
import { useParams } from "react-router-dom";
import TextareaAutosize from "react-textarea-autosize";
import { toast } from "react-toastify";
import { Edge, Node, useEdges, useNodes } from "reactflow";
import useLocalStorageState from "use-local-storage-state";
import { useDebounce } from "usehooks-ts";
import * as Y from "yjs";

import { Buddies, PromptHistory } from "@/components";
import { loadExtensions } from "@/extensions";
import { useCeramic, useQueries } from "@/hooks";
import useBuddy from "@/hooks/useBuddy";
import usePages from "@/hooks/usePages";
import { getNodePageContents, getPageIds } from "@/hooks/useQueries/pages";
import { getProjectTokenCounts } from "@/hooks/useQueries/projects";
import { LLMResponse, Page, SemanticPaper } from "@/types";

import DiffButton from "./components/DiffButton";
import SemanticResults from "./components/SemanticResults";
import styles from "./LLMSidebar.module.scss";
import { searchPapers } from "./semantic";
import turndownService from "./turndown";
import useLoadConfig from "./useLoadConfig";
import {
  addMetaToMarkdown,
  bulletList,
  handleApiError,
  listItem,
  removeContentTokens,
  setNodePageContent,
  setNodePageJson,
} from "./utils";

interface LLMSidebarProps extends React.SelectHTMLAttributes<HTMLDivElement> {
  editor: Editor;
  page: Page;
  ydoc: Y.Doc;
}

const LLMSidebar = forwardRef<HTMLDivElement, LLMSidebarProps>(
  ({ className, ydoc, editor, page, ...props }, ref) => {
    let { projectId = "global-graph", projectPublicId } = useParams();
    const queryClient = useQueryClient();
    const { address } = useCeramic();
    const { currentProject } = useQueries();
    const extensions = loadExtensions(undefined, undefined, true);
    const readOnlyEditor = useEditor({ editable: false, extensions });
    const nodes = useNodes();
    const edges = useEdges();

    const { buddy } = useBuddy();
    const { showPrompts, defaultPrompts } = useLoadConfig(page?.meta?.publicConfig);

    const scrollRef = useRef<HTMLDivElement>(null);
    const edgesMap = useRef<Y.Map<Edge>>();
    const nodesMap = useRef<Y.Map<Node>>();

    const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
    const [abortController, setAbortController] = useState(new AbortController());
    const [editorState, setEditorState] = useState(editor?.state.doc.content);
    const [loading, setLoading] = useState<boolean>(false);
    const [markdownBefore, setMarkdownBefore] = useState<string>();
    const [nodePageTokenCount, setNodePageTokenCount] = useState<number>(0);
    const [queriedNodes, setQueriedNodes] = useState<Node[]>([]);
    const [response, setResponse] = useState<LLMResponse>();
    const [responses, setResponses] = useState<string[]>([]);
    const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
    const debouncedSelectedNodeIds = useDebounce(selectedNodeIds, 500);
    const [tokenCount, setTokenCount] = useState<number>(0);
    const [apiKeyInput, setApiKeyInput] = useState<string>("");
    const [apiKeyInputVisible, setApiKeyInputVisible] = useState<boolean>(false);

    const isReadOnly = Boolean(projectPublicId);
    const debouncedEditor = useDebounce(editorState, 2000);
    const selectedNodes: Node[] = nodes?.filter((n) => n.selected);

    const [input, setInput] = useLocalStorageState<string>(
      `ldgV2Input-${projectPublicId ? projectPublicId : projectId}`,
      { defaultValue: "" }
    );
    const debouncedInput = useDebounce(input, 200);
    const [includeMeta, setIncludeMeta] = useLocalStorageState<boolean>("ldgV2includeGraphData", {
      defaultValue: isReadOnly ? true : false,
    });
    const [multiQueryType, setMultiQueryType] = useLocalStorageState<string>(
      "ldgV2multiQueryType",
      { defaultValue: "individually" }
    );
    const [includeNodePageContent, setIncludeNodePageContent] = useLocalStorageState<boolean>(
      "ldgV2includeNodePageContent",
      { defaultValue: true }
    );
    const [includeProjectContext, setIncludeProjectContext] = useLocalStorageState<boolean>(
      "ldgV2includeProjectContext",
      { defaultValue: false }
    );
    const [addToGraphNodePage, setAddToGraphNodePage] = useLocalStorageState<boolean>(
      "ldgV2addToGraphNodePage",
      { defaultValue: false }
    );
    const [apiKey, setApiKey] = useLocalStorageState<string>("ldgV2apiKey", { defaultValue: "" });

    const { data: pageIds } = useQuery({
      queryKey: ["page-ids"],
      queryFn: ({ signal }) => getPageIds(signal, currentProject?.id),
      enabled: Boolean(currentProject?.id),
      initialData: [],
    });

    // const { data: projectTokens } = useQuery({
    //   queryKey: ["project-tokens", currentProject?.slug],
    //   queryFn: ({ signal }) => getProjectTokenCounts(signal, currentProject?.slug),
    //   enabled: Boolean(currentProject?.slug),
    //   initialData: {},
    // });
    // const pageIds = Object.keys(projectTokens);

    // const { tokenCounts, pageIds, isFetching: isLoadingPageContent } = usePages();

    const { data: contents, isFetching: isLoadingPageContent } = useQuery({
      queryKey: ["node-page-contents", pageIds],
      queryFn: ({ signal }) => getNodePageContents(signal, currentProject?.id, pageIds),
      initialData: {},
      refetchOnWindowFocus: false,
    });

    useEffect(() => {
      if (selectedNodes.length == 0 && selectedNodeIds.length != 0) {
        setSelectedNodeIds([]);
        return;
      }
      const ids = selectedNodes.map((n) => n.id);
      if (ids.some((id) => !selectedNodeIds.includes(id))) {
        setSelectedNodeIds(ids);
      }
    }, [selectedNodes]);

    useEffect(() => {
      if (ydoc) {
        nodesMap.current = ydoc.getMap<Node>("nodes");
        edgesMap.current = ydoc.getMap<Edge>("edges");
      }
    }, [ydoc, nodesMap]);

    useEffect(() => {
      if (Object.values(contents).length) {
        const pageNodeIds: string[] = [];
        editor?.state.doc.descendants((node: ProseMirrorNode, pos) => {
          if (node.attrs.blockId && node.type.name == "listItem") {
            pageNodeIds.push(node.attrs.blockId);
          }
        });
        // const total = Object.values(contents).reduce((sum, content) => sum + content.tokens, 0);
        const total = Object.entries(contents).reduce((sum, [key, content]) => {
          if (pageNodeIds.includes(key)) {
            return sum + content.tokens;
          }
          return sum;
        }, 0);
        setNodePageTokenCount(total);
        onEditorChange();
      }
    }, [contents]);

    const onEditorChange = () => {
      if (selectedNodes.length > 0) return;
      let markdown = turndownService.turndown(editor?.getHTML() ?? "");
      if (includeMeta) {
        markdown = addMetaToMarkdown(markdown, nodes, edges);
      }

      let total = encode(buddy?.prompt + " " + input + " " + markdown).length;
      if (includeNodePageContent) {
        total += nodePageTokenCount;
      }

      setTokenCount(total);
    };

    useEffect(() => {
      if (debouncedEditor) {
        onEditorChange();
      }
    }, [debouncedEditor]);

    useEffect(() => {
      onEditorChange();
    }, [includeMeta, nodes, edges, includeNodePageContent, debouncedInput]);

    useEffect(() => {
      if (!editor) return;
      const onUpdate = () => {
        setEditorState(editor?.state.doc.content);
      };
      onEditorChange();
      editor.on("update", onUpdate);
      return () => {
        editor.off("update", onUpdate);
      };
    }, [editor]);

    useEffect(() => {
      if (selectedNodeIds.length > 0 && buddy) {
        let total = 0;
        let markdown = turndownService.turndown(editor?.getHTML() ?? "");
        if (includeMeta) {
          markdown = addMetaToMarkdown(markdown, nodes, edges);
        }

        for (let node of selectedNodes) {
          let tokens = encode(node?.data?.label).length;

          if (node?.id in contents) {
            tokens += contents[node?.id].tokens;
            if (includeNodePageContent) total += tokens;
          } else {
            total += tokens;
          }
          nodesMap?.current?.set(node?.id, {
            ...node,
            data: { ...node?.data, tokens },
          });
          if (multiQueryType == "individually") {
            total += encode(buddy?.prompt).length;
          }
        }
        if (multiQueryType == "collectively") total += encode(buddy?.prompt).length;
        if (includeProjectContext) total += encode(markdown).length;
        total += encode(input).length;
        setTokenCount(total);
      }
    }, [
      debouncedSelectedNodeIds,
      includeNodePageContent,
      nodePageTokenCount,
      includeProjectContext,
      multiQueryType,
      includeMeta,
      debouncedInput,
    ]);

    const replaceEditorWithMarkdown = () => {
      // TODO: Do we need the backticks?
      // const regex = /```([\s\S]+?)([`]+)?/;
      // const match = response?.response.match(regex);

      // let contentWithinBackticks = "";
      // if (match && match.length >= 2) {
      //   contentWithinBackticks = match[1];
      // }
      const contentWithinBackticks = response?.response.replace(/```/, "") ?? "";

      const htmlString = marked.parse(contentWithinBackticks);
      console.log(response, contentWithinBackticks, htmlString);
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, "text/html");
      let nanoidRegex = /^\(\|([A-Za-z0-9_-]{21})\|([01]{1})\|\): (.*)$/;

      const getIdAndNewContent = (
        text: string
      ): false | { id: string; collapsed: string; text: string } => {
        let match = text.match(nanoidRegex);
        if (match) {
          return { id: match[1], collapsed: match[2], text: match[3] };
        }
        return false;
      };

      const getValueFromListElement = (el: HTMLLIElement) => {
        let value = "";
        if (el.firstChild?.nodeValue) {
          value = el.firstChild?.nodeValue;
        } else if (el.firstChild?.nodeName == "P") {
          value = el.firstChild?.textContent ?? "";
        }
        return value;
      };

      Array.from(doc.getElementsByTagName("li")).forEach((el) => {
        const value = getValueFromListElement(el);
        const match = getIdAndNewContent(value);
        if (match) {
          el.setAttribute("blockid", match.id);
          el.setAttribute("collapsed", match.collapsed);
        } else {
          const newId = nanoid();
          el.setAttribute("blockid", newId);
          el.setAttribute("collapsed", "false");
        }
      });
      const oldParser = new DOMParser();
      const oldDoc = oldParser.parseFromString(editor?.getHTML() || "", "text/html");
      Array.from(doc.querySelectorAll('li[collapsed="1"]')).forEach((el) => {
        const blockId = el.getAttribute("blockid");
        const collapsedContent = oldDoc.querySelector(`li[blockid="${blockId}"] ul`);
        if (collapsedContent) el.append(collapsedContent);
      });

      const newBody = removeContentTokens(doc.body.innerHTML).replace(
        /<pre><code>\n<\/code><\/pre>$/gm,
        ""
      );
      console.log(newBody);
      if (editor) {
        editor.commands.setContent(newBody, true);
      }
    };

    const isSemanticDemo = buddy?.id == "4ad35e6f-49fa-4a30-8283-ef63f1d65adc";

    const [semanticResults, setSemanticResults] = useState<any[]>([]);

    const onSubmit = async (promptInput: string = input) => {
      // TEMP: Semantic demo hack
      setSemanticResults([]);
      if (isSemanticDemo) {
        setLoading(true);
        try {
          const response = await searchPapers(promptInput);
          if (!response.data.data) window.alert("No results found");
          setSemanticResults(response.data.data);
          setLoading(false);
        } catch (error) {
          setLoading(false);
          window.alert(error);
        }
        return;
      }

      if (selectedNodes.length > 0 && multiQueryType == "individually") {
        queryMultipleNodes(promptInput);
        return;
      }

      let markdown = turndownService.turndown(editor?.getHTML() ?? "");
      if (includeMeta) {
        markdown = addMetaToMarkdown(markdown, nodes, edges);
      }

      setMarkdownBefore(markdown);
      const newAbortController = new AbortController();
      setAbortController(newAbortController);
      setLoading(true);
      setResponse({ prompt: "", response: "" });
      readOnlyEditor?.commands.setContent("");
      try {
        if (selectedNodes.length) {
          console.log("selectedNodes", selectedNodes);
          const nodeContent = selectedNodes
            .map(
              (n) =>
                `Label: ${n?.data?.label}${
                  includeNodePageContent ? `\nContent: ${contents[n?.id]?.content ?? ""}` : ""
                }`
            )
            .join("\n\n");
          if (includeProjectContext) {
            markdown += `\n\n# Selected Nodes:\n\n${nodeContent}`;
          } else {
            markdown = nodeContent;
          }
        } else if (includeNodePageContent) {
          markdown += `\n\n# Node page contents:\n`;
          for (const [id, value] of Object.entries(contents)) {
            const node = nodes.find((n) => n.id == id);
            if (!node) continue;
            markdown += `ID: ${id}\nLabel: ${node?.data?.label}\nContent: ${value.content}\n\n`;
          }
        }
        console.log(buddy?.prompt.replace("{editor_content}", markdown));
        const key = apiKey ? { openai_key: apiKey } : {};
        const streamResponse = await fetch(import.meta.env.VITE_DJANGO_URL + "llm/chat/", {
          method: "post",
          credentials: "include",
          signal: newAbortController.signal,
          body: JSON.stringify({
            address: address,
            projectId: projectId,
            input: promptInput,
            gpt4: buddy?.model == "gpt-4",
            prompt: buddy?.prompt.replace("{editor_content}", markdown),
            ...key,
          }),
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });
        const reader = streamResponse?.body?.getReader();
        const decoder = new TextDecoder();
        setResponse({ response: "", prompt: "" });
        setResponses([]);
        setQueriedNodes([]);
        if (reader) {
          let string = "";
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              setLoading(false);
              return;
            }
            const decoded = decoder.decode(value);
            string += decoded;
            setResponse({ response: string, prompt: "" });

            readOnlyEditor?.commands.setContent(
              DOMPurify.sanitize(marked.parse(removeContentTokens(string)))
            );
            let elem = scrollRef?.current;
            if (elem) elem.scrollTop = elem?.scrollHeight;
          }
        }
      } catch (error) {
        handleApiError(error);
        setLoading(false);
      }
    };

    const queryMultipleNodes = async (promptInput: string = input) => {
      if (!buddy) return alert("No buddy selected");
      let i = 0;
      setResponses(Array(selectedNodes?.length).fill(""));
      const newResponses = Array(selectedNodes?.length).fill("");
      setQueriedNodes(selectedNodes);
      try {
        const newAbortController = new AbortController();
        setAbortController(newAbortController);
        setLoading(true);

        for (let node of selectedNodes) {
          let editorContent = "";

          if (includeProjectContext) {
            editorContent = turndownService.turndown(editor?.getHTML() ?? "");
            if (includeMeta) {
              editorContent = addMetaToMarkdown(editorContent, nodes, edges);
            }
          }

          editorContent += "\n\n# Selected Node: \n";
          editorContent += `\nLabel: ${node?.data?.label}`;
          if (node?.id in contents && includeNodePageContent) {
            editorContent += `\nContent: ${contents[node?.id]?.content ?? ""}`;
          }

          console.log(buddy?.prompt.replace("{editor_content}", editorContent));
          const key = apiKey ? { openai_key: apiKey } : {};
          console.log("promptInput", buddy?.prompt.replace("{editor_content}", editorContent));
          const streamResponse = await fetch(import.meta.env.VITE_DJANGO_URL + "llm/chat/", {
            method: "post",
            credentials: "include",
            signal: newAbortController.signal,
            body: JSON.stringify({
              address: address,
              projectId: projectId,
              input: promptInput,
              gpt4: buddy?.model == "gpt-4",
              prompt: buddy?.prompt.replace("{editor_content}", editorContent),
              ...key,
            }),
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          });
          const reader = streamResponse?.body?.getReader();
          const decoder = new TextDecoder();
          if (reader) {
            let string = "";
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                break;
              }
              const decoded = decoder.decode(value);
              string += decoded;
              newResponses[i] = DOMPurify.sanitize(marked.parse(string));
              setResponses(newResponses);
              forceUpdate();
              let elem = scrollRef?.current;
              if (elem) elem.scrollTop = elem?.scrollHeight;
            }
          }
          i = i + 1;
        }
        setLoading(false);
      } catch (error) {
        handleApiError(error);
        setLoading(false);
      }
    };

    const addIndividualResponsesToGraph = () => {
      const newContent: JSONContent[] = [
        { type: "paragraph" },
        {
          type: "paragraph",
          content: [{ type: "text", marks: [{ type: "bold" }], text: "Batch query results:" }],
        },
      ];

      responses.forEach((response, i) => {
        const blockId = nanoid();
        newContent.push(bulletList([listItem(blockId, `LLM Response ${i + 1}`)]));

        if (addToGraphNodePage) {
          const roomName = `lateral-ldg-${currentProject?.slug}-${blockId}`;
          const responseJson = generateJSON(response, extensions);
          setNodePageContent(responseJson, roomName, editor.schema);
        } else {
          const responseJson = generateJSON(response, extensions);
          newContent.push(...responseJson["content"]);
        }

        const selectedNode = queriedNodes?.[i];

        const node: Node = {
          id: blockId,
          data: { label: response },
          position: {
            x: selectedNode?.positionAbsolute?.x + 100,
            y: selectedNode?.positionAbsolute?.y + 100,
          },
          type: "GraphNode",
          expandParent: false,
          style: {
            width: 200,
            height: 80,
          },
        };
        nodesMap?.current?.set(blockId, node);
        const id = `edge-${selectedNode?.id}-${blockId}`;
        edgesMap?.current?.set(id, {
          id,
          source: selectedNode?.id,
          target: blockId,
          targetHandle: "target-top",
          sourceHandle: "target-bottom",
        } as Edge);
      });

      // newContent.push(bulletList(list));
      // console.log(list, bulletList(list), newContent);
      editor.commands.insertContentAt(editor.view.state.doc.content.size, newContent);

      queryClient.invalidateQueries({ queryKey: ["pages", currentProject?.id] });
      queryClient.invalidateQueries({ queryKey: ["page-ids"] });
      queryClient.invalidateQueries({ queryKey: ["project-tokens"] });
    };

    const addCollectiveResponseToGraph = () => {
      const blockId = nanoid();
      const text = readOnlyEditor?.getText();

      let newContent = [{ type: "paragraph" }, bulletList([listItem(blockId, text)])];

      if (addToGraphNodePage) {
        const content = readOnlyEditor?.getJSON()["content"] ?? [];
        const roomName = `lateral-ldg-${currentProject?.slug}-${blockId}`;
        setNodePageJson(content, roomName, editor.schema);
        newContent = [{ type: "paragraph" }, bulletList([listItem(blockId, "LLM Response")])];
      }
      editor.commands.insertContentAt(editor.view.state.doc.content.size, newContent);

      if (!selectedNodes.length) return;

      const node: Node = {
        id: blockId,
        data: { label: text },
        position: {
          x: selectedNodes[0]?.positionAbsolute?.x + 100,
          y: selectedNodes[0]?.positionAbsolute?.y + 100,
        },
        type: "GraphNode",
        expandParent: false,
        style: {
          width: 200,
          height: 80,
        },
      };
      nodesMap?.current?.set(blockId, node);
      selectedNodes.forEach((node) => {
        const id = `edge-${node?.id}-${blockId}`;
        edgesMap?.current?.set(id, {
          id,
          source: node.id,
          target: blockId,
          targetHandle: "target-top",
          sourceHandle: "target-bottom",
        } as Edge);
      });

      queryClient.invalidateQueries({ queryKey: ["pages", currentProject?.id] });
      queryClient.invalidateQueries({ queryKey: ["page-ids"] });
      queryClient.invalidateQueries({ queryKey: ["project-tokens"] });
    };

    const defaultPromptsEl = showPrompts && (
      <div
        className={classNames(styles.suggestedPrompts, {
          [styles.borderBottom]: Boolean(response?.response),
        })}
      >
        <h2>Suggested prompts</h2>
        {defaultPrompts.map(
          (prompt, i) =>
            Boolean(prompt) && (
              <div
                className={styles.suggestedPrompt}
                key={`suggested-prompt-${i}`}
                onClick={async () => {
                  setInput(prompt);
                  onSubmit(prompt);
                }}
              >
                {prompt}
              </div>
            )
        )}
      </div>
    );

    const onToggleApiKey = () => {
      if (apiKeyInputVisible) {
        setApiKeyInputVisible(false);
      } else {
        setApiKeyInputVisible(true);
        setApiKeyInput(apiKey);
      }
    };

    const onSaveApiKey = () => {
      setApiKey(apiKeyInput);
      setApiKeyInputVisible(false);
      toast.success("API Key saved");
    };

    return (
      <div className={classNames(className, styles.wrapper)}>
        <div className={styles.response} ref={scrollRef}>
          {defaultPromptsEl}
          {isSemanticDemo ? (
            <SemanticResults
              results={semanticResults as SemanticPaper[]}
              editor={editor}
              extensions={extensions}
              selectedNodes={selectedNodes}
              nodesMap={nodesMap?.current}
              edgesMap={edgesMap?.current}
              prompt={input}
            />
          ) : (
            <div
              className={classNames(styles.responseBody, {
                [styles.noResponse]: !Boolean(response?.response),
              })}
            >
              {responses?.length && responses?.[0].length && queriedNodes?.length ? (
                <div className={styles.responses}>
                  {responses.map(
                    (response, i) =>
                      Boolean(response.length) && (
                        <div className={styles.multiResponse} key={`response-${i}`}>
                          <div className={styles.nodeLabel}>{queriedNodes?.[i]?.data?.label}</div>
                          <div
                            className={styles.responseBody}
                            dangerouslySetInnerHTML={{ __html: response }}
                          ></div>
                        </div>
                      )
                  )}
                </div>
              ) : (
                <EditorContent editor={readOnlyEditor} />
              )}
            </div>
          )}
          <footer>
            {loading ? (
              <div className={styles.loader}>
                <div className={styles.star}>
                  <FontAwesomeIcon icon={faStars} />
                </div>
                <div className={styles.loadingText}>
                  Generating&hellip; <FontAwesomeIcon icon={faLoader} spin />
                </div>
              </div>
            ) : (
              <></>
            )}
            {!isSemanticDemo && (
              <div className={styles.buttons}>
                {loading ? (
                  <button onClick={() => abortController.abort()}>Stop</button>
                ) : isReadOnly ? (
                  <></>
                ) : (responses?.length && responses?.[0].length) ||
                  (response?.response?.length && selectedNodes.length) ? (
                  <div className={styles.addButton}>
                    <button
                      onClick={() => {
                        if (responses?.length && responses?.[0].length)
                          addIndividualResponsesToGraph();
                        else if (response?.response?.length) addCollectiveResponseToGraph();
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      Add to Graph
                    </button>
                    <label>
                      <input
                        type="checkbox"
                        name="addToGraphNodePage"
                        checked={addToGraphNodePage}
                        onChange={(e) => setAddToGraphNodePage(e?.target?.checked)}
                      />
                      Add to node page
                    </label>
                  </div>
                ) : (
                  Boolean(response?.response?.length) && (
                    <>
                      <button onClick={replaceEditorWithMarkdown}>
                        <FontAwesomeIcon icon={faRotate} />
                        Replace
                      </button>
                      <DiffButton markdownBefore={markdownBefore} response={response} />
                    </>
                  )
                )}
              </div>
            )}
          </footer>
        </div>
        <div className={styles.input}>
          <div className={styles.promptMeta}>
            <div className={styles.metaLeft}>
              <Dialog.Root>
                <Dialog.Trigger asChild>
                  <button className={styles.buddy}>
                    <div>
                      <FontAwesomeIcon
                        icon={faRobot}
                        style={buddy ? { color: buddy?.colour?.toString(16) } : {}}
                        height="16"
                      />
                      <span>{buddy?.name}</span>
                    </div>
                  </button>
                </Dialog.Trigger>
                <Dialog.Portal>
                  <Dialog.Overlay className={styles.dialogOverlay} />
                  <Dialog.Content className={classNames(styles.dialogContent, styles.buddies)}>
                    <Buddies />
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>
              {selectedNodes.length > 0 && (
                <div
                  className={styles.selectedNodesControls}
                  onClick={() => {
                    if (multiQueryType == "individually") {
                      setMultiQueryType("collectively");
                    }
                    if (multiQueryType == "collectively") {
                      setMultiQueryType("individually");
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faArrowsToCircle} className={styles.mainIcon} />
                  {selectedNodes?.length == 1
                    ? "Query selected node"
                    : `Query ${selectedNodes?.length} selected nodes`}{" "}
                  <span>{multiQueryType}</span>
                  <FontAwesomeIcon icon={faCaretDown} className={styles.downIcon} />
                </div>
              )}
            </div>

            <Dialog.Root>
              <Dialog.Trigger asChild>
                <button className={styles.history}>
                  <FontAwesomeIcon icon={faHistory} width="16" height="16" />
                </button>
              </Dialog.Trigger>
              <Dialog.Portal>
                <Dialog.Overlay className={styles.dialogOverlay} />
                <Dialog.Content className={classNames(styles.dialogContent, styles.prompts)}>
                  <PromptHistory />
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </div>
          {!apiKeyInputVisible ? (
            <div className={styles.textarea}>
              <TextareaAutosize
                onChange={(e) => setInput(e.target?.value)}
                onKeyDown={(e) => {
                  if (e.keyCode == 13 && e.shiftKey == false) {
                    e.preventDefault();
                    onSubmit();
                  }
                }}
                placeholder="Send a message"
                className={styles.inputTextarea}
                value={input}
                maxRows={7}
              />
              <button
                className={styles.send}
                disabled={input.length == 0}
                onClick={() => onSubmit()}
              >
                <FontAwesomeIcon icon={faSend} />
              </button>
            </div>
          ) : (
            <div className={styles.apiKeyInput}>
              <input
                type="text"
                placeholder="Enter GPT API Key"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target?.value)}
              />
              <button
                className={styles.send}
                disabled={
                  !(apiKeyInput.length == 0 || Boolean(apiKeyInput.match(/^sk-[A-Za-z0-9]{48}$/)))
                }
                onClick={() => onSaveApiKey()}
              >
                <FontAwesomeIcon icon={faFloppyDisk} />
              </button>
            </div>
          )}
          <div className={styles.inputMeta}>
            <span
              className={classNames(styles.tokenCount, {
                [styles.red]: !isLoadingPageContent && tokenCount > 128000,
              })}
            >
              Tokens{" "}
              {isLoadingPageContent ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                tokenCount.toLocaleString()
              )}
            </span>
            <div className={styles.divider}></div>
            Include
            <label>
              <input
                type="checkbox"
                name="selected"
                checked={includeNodePageContent}
                onChange={(e) => setIncludeNodePageContent(e?.target?.checked)}
              />
              Node Pages
            </label>
            <label>
              <input
                type="checkbox"
                name="graph"
                checked={includeMeta}
                onChange={(e) => setIncludeMeta(e?.target?.checked)}
              />
              Meta
            </label>
            {selectedNodes?.length > 0 && (
              <label>
                <input
                  type="checkbox"
                  name="selected"
                  checked={includeProjectContext}
                  onChange={(e) => setIncludeProjectContext(e?.target?.checked)}
                />
                Full Context
              </label>
            )}
            <button className={styles.apiKey} onClick={onToggleApiKey}>
              <FontAwesomeIcon icon={faCog} size="sm" /> API Key
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default LLMSidebar;
